/**
 * Pure summary helpers over finished runs: counts of successes and failures
 * plus the one-line label for the panel header. No I/O and no clock access —
 * callers pass time in so everything here stays deterministic and testable.
 */
import type { RunningTask, RunSummary } from './data';

export interface RunStats { done: number; failed: number; lastAt: string | null; label: string }

/** "just now" under a minute, then whole minutes, hours, days. Future times read as "just now". */
export function agoLabel(ageMs: number): string {
  if (ageMs < 60_000) return 'just now';
  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

/** Counts of finished runs and a one-line summary for the panel header. */
export function runStats(results: readonly RunSummary[], nowMs: number): RunStats {
  let done = 0;
  let failed = 0;
  let lastAt: string | null = null;
  let lastMs = Number.NaN;
  for (const r of results) {
    if (r.success) done += 1;
    else failed += 1;
    const ms = Date.parse(r.reported_at);
    if (Number.isNaN(ms)) continue;
    if (Number.isNaN(lastMs) || ms > lastMs) {
      lastMs = ms;
      lastAt = r.reported_at;
    }
  }
  let label = results.length === 0 ? '' : `${done} done`;
  if (failed > 0) label += `, ${failed} failed`;
  if (lastAt !== null) label += ` · last ${agoLabel(nowMs - lastMs)}`;
  return { done, failed, lastAt, label };
}

/** How long a run has been going: "just started" under a minute, then minutes, hours, days. */
export function runningFor(startedAt: string | null, nowMs: number): string {
  if (startedAt === null) return 'running';
  const started = Date.parse(startedAt);
  if (Number.isNaN(started)) return 'running';
  const ageMs = nowMs - started;
  if (ageMs < 60_000) return 'just started';
  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 60) return `running ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `running ${hours} h`;
  return `running ${Math.floor(hours / 24)} d`;
}

/** What a run reads as once its own limit has passed. */
export const PAST_ITS_LIMIT = 'past its limit';

/**
 * How long a run has left before its own limit: "" when the run carries no usable
 * limit, "past its limit" once the time is up, then minutes, hours, days.
 */
export function timeLeft(deadlineAt: string | null, nowMs: number): string {
  if (deadlineAt === null) return '';
  const deadline = Date.parse(deadlineAt);
  if (Number.isNaN(deadline)) return '';
  const leftMs = deadline - nowMs;
  if (leftMs <= 0) return PAST_ITS_LIMIT;
  if (leftMs < 60_000) return 'under a minute left';
  const minutes = Math.floor(leftMs / 60_000);
  if (minutes < 60) return `${minutes} min left`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h left`;
  return `${Math.floor(hours / 24)} d left`;
}

/** A run is worth a nudge once it has been going far longer than a task normally takes. */
export const STUCK_AFTER_MS = 30 * 60_000;

/** Runs that have been going long enough that the owner may want to stop them. */
export function stuckRuns(running: readonly RunningTask[], nowMs: number): RunningTask[] {
  return running.filter((r) => {
    if (r.started_at === null) return false;
    const started = Date.parse(r.started_at);
    return !Number.isNaN(started) && nowMs - started >= STUCK_AFTER_MS;
  });
}

/** Runs the engine is about to expire, so the panel can flag them before it happens. */
export function pastDeadline(running: readonly RunningTask[], nowMs: number): RunningTask[] {
  return running.filter((r) => timeLeft(r.deadline_at, nowMs) === PAST_ITS_LIMIT);
}
